import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import NRRiseAiIcon from '../../../NRIcons/NRRiseAiIcon';
import NRAIMorphSpinner, { CROSSFADE_DURATION, ROTATION_STOP_DURATION } from '../../NRAIMorphSpinner/NRAIMorphSpinner';
import NRAIThinkingButton from '../../NRAIThinkingButton/NRAIThinkingButton';
import { SUGGESTED_PROMPTS } from '../prompts';

export default function WelcomeScreen({ isLoadingSession, sessionError, onPromptClick }) {
  const theme = useTheme();
  const [showSpinner, setShowSpinner] = useState(false);
  const [spinnerComplete, setSpinnerComplete] = useState(false);
  const [textOpacity, setTextOpacity] = useState(1);
  const [spinnerOpacity, setSpinnerOpacity] = useState(1);
  const fadeOutTimerRef = useRef(null);

  // Show spinner when loading starts
  useEffect(() => {
    if (isLoadingSession) {
      setShowSpinner(true);
      setSpinnerComplete(false);
      setTextOpacity(1);
      setSpinnerOpacity(1);
      if (fadeOutTimerRef.current) {
        clearTimeout(fadeOutTimerRef.current);
        fadeOutTimerRef.current = null;
      }
    }
  }, [isLoadingSession]);

  // Complete spinner when loading finishes
  useEffect(() => {
    if (!isLoadingSession && showSpinner && !spinnerComplete) {
      setSpinnerComplete(true);
      // Fade out text immediately when completion starts
      setTextOpacity(0);
    }
  }, [isLoadingSession, showSpinner, spinnerComplete]);

  // Handle spinner morphing completion
  const handleSpinnerComplete = () => {
    // After morphing completes, wait a bit then fade out the chevron
    fadeOutTimerRef.current = setTimeout(() => {
      setSpinnerOpacity(0);
      // After fade out completes, remove the spinner
      setTimeout(() => {
        setShowSpinner(false);
        setSpinnerComplete(false);
        setTextOpacity(1);
        setSpinnerOpacity(1);
      }, CROSSFADE_DURATION); // Match fade out duration
    }, ROTATION_STOP_DURATION); // Small delay after morphing completes
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (fadeOutTimerRef.current) {
        clearTimeout(fadeOutTimerRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {/* Session loading indicator */}
      {showSpinner && (
        <Box 
          sx={{ 
            mb: 2, 
            display: 'flex', 
            alignItems: 'center',
            opacity: spinnerOpacity,
            transition: `opacity ${CROSSFADE_DURATION}ms ease-out`,
            '& > *': {
              '& .MuiTypography-root': {
                opacity: textOpacity,
                transition: `opacity ${CROSSFADE_DURATION}ms ease-out`
              }
            }
          }}
        >
          <NRAIMorphSpinner 
            size={16} 
            text={
              <NRAIThinkingButton
                isThinking={!spinnerComplete}
                label="Initializing session..."
                thinkingLabel="Initializing session..."
                color={theme.palette.text.secondary}
                showChevron={false}
                asText={true}
              />
            }
            complete={spinnerComplete}
            onComplete={handleSpinnerComplete}
          />
        </Box>
      )}

      {/* Session error indicator */}
      {sessionError && !isLoadingSession && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.contrastText,
            borderRadius: theme.shape.borderRadius * 2,
            maxWidth: '500px'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Session Error
          </Typography>
          <Typography variant="caption">{sessionError}</Typography>
        </Paper>
      )}

      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <NRRiseAiIcon height="48" width="48" color={theme.palette.riseAiPurple} />
      </Box>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Welcome. I have analyzed your software asset.
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary, textAlign: 'center', maxWidth: '400px' }}>
        I'm ready to help you validate findings, explain complex issues, and build remediation workflows. Below are some prompts to help start, or you
        can ask anything related to software security.
      </Typography>

      {/* Prompt Cards */}
      <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
        {SUGGESTED_PROMPTS.map((prompt, index) => (
          <Card
            key={index}
            variant="outlined"
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '250px',
              '&:hover': {
                boxShadow: theme.shadows[4],
                borderColor: theme.palette.riseAiPurple
              }
            }}
            onClick={() => onPromptClick(prompt.text)}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.action.selected,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: theme.typography.body1.fontSize
                  }}
                >
                  <prompt.icon height="20" width="20" color={theme.palette.text.primary} />
                </Box>
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {prompt.title}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {prompt.text}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
